import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  HEALTH_DOC_KINDS,
  HEALTH_DOC_REASON_CATEGORIES,
  type BirthNotification,
  type BirthSex,
  type Facility,
  type HealthDocKind,
  type HealthDocVaultLink,
  type Page,
  type VaccinationRecord,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TAB_KEYS = ['vaccinations', 'health-documents', 'births'] as const;
type TabKey = (typeof TAB_KEYS)[number];

/** Vaccinations, health documents (discharge summaries / medical certificates) and birth notifications — three smaller m24-ehealth resources grouped onto one screen. */
export function ClinicalRecords() {
  const [tab, setTab] = useState<TabKey>('vaccinations');
  return (
    <div>
      <PageHeader title="Clinical Records" subtitle="Vaccinations, discharge summaries / medical certificates, and birth notifications." />
      <Tabs
        tabs={[
          { key: 'vaccinations', label: 'Vaccinations' },
          { key: 'health-documents', label: 'Health documents' },
          { key: 'births', label: 'Birth notifications' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'vaccinations' ? <VaccinationsTab /> : null}
      {tab === 'health-documents' ? <HealthDocumentsTab /> : null}
      {tab === 'births' ? <BirthsTab /> : null}
    </div>
  );
}

function VaccinationsTab() {
  const { persona } = useAuth();
  const isClinician = persona?.roles.includes('CLINICIAN') ?? false;
  const facilitiesQ = useApiQuery<Page<Facility>>('/api/v1/m24/facilities?limit=100');
  const [ninFilter, setNinFilter] = useState('');
  const listQ = useApiQuery<Page<VaccinationRecord>>(`/api/v1/m24/vaccination-records?limit=50${ninFilter ? `&nin=${encodeURIComponent(ninFilter)}` : ''}`);

  const [nin, setNin] = useState('');
  const [antigen, setAntigen] = useState('');
  const [doseNumber, setDoseNumber] = useState('1');
  const [administeredAt, setAdministeredAt] = useState('');
  const [batchNo, setBatchNo] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m24/vaccination-records', { nin, antigen, doseNumber: Number.parseInt(doseNumber, 10), administeredAt, batchNo, facilityId });
      setNin('');
      setAntigen('');
      setBatchNo('');
      listQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record vaccination');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <div className="mb-3 flex gap-2">
          <input className="field-input !w-56" placeholder="Filter by NIN…" value={ninFilter} onChange={(e) => setNinFilter(e.target.value)} />
        </div>
        {listQ.error ? <ErrorNote message={listQ.error} /> : null}
        <div className="card overflow-x-auto">
          {listQ.loading ? (
            <Spinner />
          ) : (listQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No vaccination records" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">NIN</th>
                  <th className="table-head">Antigen</th>
                  <th className="table-head">Dose</th>
                  <th className="table-head">Administered</th>
                  <th className="table-head">Cert</th>
                </tr>
              </thead>
              <tbody>
                {listQ.data!.items.map((v) => (
                  <tr key={v.id} className="hover:bg-slate-50">
                    <td className="table-cell font-medium">{v.nin}</td>
                    <td className="table-cell">{v.antigen}</td>
                    <td className="table-cell">{v.doseNumber}</td>
                    <td className="table-cell text-xs">{formatDate(v.administeredAt)}</td>
                    <td className="table-cell">
                      <StatusBadge status={v.certStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Record a dose</h2>
        {!isClinician ? <ErrorNote message="Requires the CLINICIAN role." /> : null}
        {error ? (
          <div className="my-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <div className="space-y-2.5">
          <Field label="Patient NIN">
            <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
          </Field>
          <Field label="Antigen">
            <input className="field-input" value={antigen} onChange={(e) => setAntigen(e.target.value)} placeholder="e.g. COVID-19 mRNA" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Dose #">
              <input type="number" min="1" max="20" className="field-input" value={doseNumber} onChange={(e) => setDoseNumber(e.target.value)} />
            </Field>
            <Field label="Administered">
              <input type="date" className="field-input" value={administeredAt} onChange={(e) => setAdministeredAt(e.target.value)} />
            </Field>
          </div>
          <Field label="Batch no.">
            <input className="field-input" value={batchNo} onChange={(e) => setBatchNo(e.target.value)} />
          </Field>
          <Field label="Facility">
            <select className="field-input" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
              <option value="">Select a facility…</option>
              {(facilitiesQ.data?.items ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </Field>
          <button
            className="btn-primary w-full"
            onClick={submit}
            disabled={busy || !isClinician || nin.trim().length < 5 || !antigen.trim() || !administeredAt || !batchNo.trim() || !facilityId}
          >
            {busy ? 'Recording…' : 'Record & issue certificate'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HealthDocumentsTab() {
  const { persona } = useAuth();
  const isClinician = persona?.roles.includes('CLINICIAN') ?? false;
  const facilitiesQ = useApiQuery<Page<Facility>>('/api/v1/m24/facilities?limit=100');

  const [nin, setNin] = useState('');
  const [kind, setKind] = useState<HealthDocKind>('MEDICAL_CERTIFICATE');
  const [facilityId, setFacilityId] = useState('');
  const [reasonCategory, setReasonCategory] = useState('ILLNESS');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<HealthDocVaultLink | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const doc = await api.post<HealthDocVaultLink>('/api/v1/m24/health-documents', {
        nin,
        kind,
        facilityId: facilityId || undefined,
        reasonCategory,
        validFrom: kind === 'MEDICAL_CERTIFICATE' ? validFrom : undefined,
        validTo: kind === 'MEDICAL_CERTIFICATE' ? validTo : undefined,
      });
      setIssued(doc);
      setNin('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to issue document');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Issue a document</h2>
        {!isClinician ? <ErrorNote message="Requires the CLINICIAN role." /> : null}
        {error ? (
          <div className="my-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <div className="space-y-2.5">
          <Field label="Patient NIN">
            <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
          </Field>
          <Field label="Kind">
            <select className="field-input" value={kind} onChange={(e) => setKind(e.target.value as HealthDocKind)}>
              {HEALTH_DOC_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reason category">
            <select className="field-input" value={reasonCategory} onChange={(e) => setReasonCategory(e.target.value)}>
              {HEALTH_DOC_REASON_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          {kind === 'MEDICAL_CERTIFICATE' ? (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Valid from">
                <input type="date" className="field-input" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
              </Field>
              <Field label="Valid to">
                <input type="date" className="field-input" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
              </Field>
            </div>
          ) : null}
          <Field label="Facility (optional)">
            <select className="field-input" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
              <option value="">Not specified</option>
              {(facilitiesQ.data?.items ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </Field>
          <button
            className="btn-primary w-full"
            onClick={submit}
            disabled={busy || !isClinician || nin.trim().length < 5 || (kind === 'MEDICAL_CERTIFICATE' && (!validFrom || !validTo))}
          >
            {busy ? 'Issuing…' : 'Issue document'}
          </button>
        </div>
        {issued ? (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
            Issued — serial <code className="rounded bg-white/60 px-1 py-0.5">{issued.docVaultSerial}</code>, id {issued.id}
          </div>
        ) : null}
      </div>
      <LookupRevokePanel isClinician={isClinician} />
    </div>
  );
}

function LookupRevokePanel({ isClinician }: { isClinician: boolean }) {
  const [id, setId] = useState('');
  const [doc, setDoc] = useState<HealthDocVaultLink | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lookUp = async () => {
    setBusy(true);
    setError(null);
    setDoc(null);
    try {
      setDoc(await api.get<HealthDocVaultLink>(`/api/v1/m24/health-documents/${encodeURIComponent(id.trim())}`));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post<HealthDocVaultLink>(`/api/v1/m24/health-documents/${id.trim()}/revoke`, { reason });
      setDoc(updated);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Look up / revoke by id</h2>
      <p className="mb-3 text-xs text-slate-500">
        m24-ehealth exposes no staff-facing list of every health document (only /me for the citizen and /:id for a single lookup) — look up by
        the id shown when a document is issued.
      </p>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="flex gap-2">
        <input className="field-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="Document id…" />
        <button className="btn-secondary shrink-0" onClick={lookUp} disabled={busy || !id.trim()}>
          Look up
        </button>
      </div>
      {doc ? (
        <div className="mt-3 rounded-lg border border-slate-200 p-3 text-sm">
          <div className="mb-1 flex items-center justify-between">
            <span className="font-semibold">{doc.kind.replaceAll('_', ' ')}</span>
            <StatusBadge status={doc.status} />
          </div>
          <div className="text-xs text-slate-500">Patient: {doc.nin}</div>
          {doc.validFrom ? (
            <div className="text-xs text-slate-500">
              Valid {formatDate(doc.validFrom)} → {formatDate(doc.validTo)}
            </div>
          ) : null}
          {doc.status === 'VALID' && isClinician ? (
            <div className="mt-2 flex gap-2">
              <input className="field-input" placeholder="Revoke reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
              <button className="btn-danger shrink-0 !px-2.5 !py-1 text-xs" disabled={reason.trim().length < 3 || busy} onClick={revoke}>
                Revoke
              </button>
            </div>
          ) : null}
          {doc.revokedReason ? <p className="mt-2 text-xs text-rose-600">Revoked: {doc.revokedReason}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function BirthsTab() {
  const { persona } = useAuth();
  const canRecord = (persona?.roles.includes('CLINICIAN') || persona?.roles.includes('RECEPTION')) ?? false;
  const facilitiesQ = useApiQuery<Page<Facility>>('/api/v1/m24/facilities?limit=100');
  const listQ = useApiQuery<Page<BirthNotification>>('/api/v1/m24/birth-notifications?limit=50');

  const [motherNin, setMotherNin] = useState('');
  const [childName, setChildName] = useState('');
  const [sex, setSex] = useState<BirthSex>('F');
  const [dob, setDob] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m24/birth-notifications', { motherNin, childName: childName || undefined, sex, dob, facilityId });
      setMotherNin('');
      setChildName('');
      listQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record birth');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="lg:col-span-2">
        {listQ.error ? <ErrorNote message={listQ.error} /> : null}
        <div className="card overflow-x-auto">
          {listQ.loading ? (
            <Spinner />
          ) : (listQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No birth notifications yet" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Mother NIN</th>
                  <th className="table-head">Child</th>
                  <th className="table-head">Sex</th>
                  <th className="table-head">DOB</th>
                  <th className="table-head">Push to M01</th>
                </tr>
              </thead>
              <tbody>
                {listQ.data!.items.map((b) => (
                  <tr key={b.id} className="hover:bg-slate-50">
                    <td className="table-cell font-medium">{b.motherNin}</td>
                    <td className="table-cell">{b.childName ?? '—'}</td>
                    <td className="table-cell">{b.sex}</td>
                    <td className="table-cell text-xs">{formatDate(b.dob)}</td>
                    <td className="table-cell">
                      <StatusBadge status={b.pushStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Record a live birth</h2>
        {!canRecord ? <ErrorNote message="Requires CLINICIAN or RECEPTION." /> : null}
        {error ? (
          <div className="my-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <div className="space-y-2.5">
          <Field label="Mother NIN">
            <input className="field-input" value={motherNin} onChange={(e) => setMotherNin(e.target.value)} />
          </Field>
          <Field label="Child name (optional)">
            <input className="field-input" value={childName} onChange={(e) => setChildName(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Sex">
              <select className="field-input" value={sex} onChange={(e) => setSex(e.target.value as BirthSex)}>
                <option value="F">F</option>
                <option value="M">M</option>
              </select>
            </Field>
            <Field label="Date of birth">
              <input type="date" className="field-input" value={dob} onChange={(e) => setDob(e.target.value)} />
            </Field>
          </div>
          <Field label="Facility">
            <select className="field-input" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
              <option value="">Select a facility…</option>
              {(facilitiesQ.data?.items ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </Field>
          <button
            className="btn-primary w-full"
            onClick={submit}
            disabled={busy || !canRecord || motherNin.trim().length < 5 || !dob || !facilityId}
          >
            {busy ? 'Recording…' : 'Record birth'}
          </button>
          <p className="text-[11px] text-slate-400">
            M01 Civil Registry has no live intake endpoint yet — the push status will show PENDING_DISPATCH until that integration exists.
          </p>
        </div>
      </div>
    </div>
  );
}
